import { Component, Inject, OnInit, OnDestroy, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Organization, OrgHat, OrgPart, OrgUser, OrgPolicy, PolicyStatus } from '@newgenus/common';
import { MaterialModule } from '../../../material.module';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { NestedTreeControl } from '@angular/cdk/tree';
import { NormalizePipe } from '../../format-pipe';
import { OrgSearchBarComponent } from '../../shared/org-search-bar/org-search-bar.component';
import { OrgBoardService } from '../../services/org-board-service';
import { Subject, takeUntil } from 'rxjs';
import fuzzysort from 'fuzzysort';

// ============================================================
// INTERFACES
// ============================================================

export interface HatExplorerDialogData {
    organization: Organization;
}

/**
 * Generic tree node for the explorer views
 */
export interface ExplorerTreeNode {
    key: string;
    name: string;
    type: 'position' | 'hat' | 'user' | 'policy' | 'security-group' | 'label';
    icon: string;
    children: ExplorerTreeNode[];
    /** Additional details to show (e.g., part type, user count) */
    details?: string;
    /** Part hierarchy path for positions */
    hierarchy?: string;
    /** Whether this node matches the current search */
    matchesSearch?: boolean;
    /** Policy status (only for policy nodes) */
    status?: PolicyStatus;
    /** Whether this hat has special rights (only for hat nodes) */
    isSpecialRights?: boolean;
    /** Part type for positions (e.g., 'post', 'section', 'unit') */
    partType?: string;
    /** Hat-level counts for filtering (only for hat nodes) */
    userCount?: number;
    policyCount?: number;
    securityGroupCount?: number;
    positionCount?: number;
    isMultiUsers?: boolean;
    isPostHat?: boolean;
}

/**
 * View mode for the explorer
 */
export type ExplorerViewMode = 'positions' | 'hats';

// ============================================================
// COMPONENT
// ============================================================

@Component({
    selector: 'shared-org-hat-explorer-dialog',
    imports: [MaterialModule, CommonModule, FormsModule, MatTreeModule, NormalizePipe, OrgSearchBarComponent],
    templateUrl: './org-hat-explorer-dialog.html',
    styleUrls: ['./org-hat-explorer-dialog.scss', '../org-dialog.scss'],
})
export class OrgHatExplorerDialogComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    private obService = inject(OrgBoardService);

    // === View Mode ===
    public viewMode: ExplorerViewMode = 'positions';

    // === Position View Tree ===
    public positionTreeControl = new NestedTreeControl<ExplorerTreeNode>((node) => node.children);
    public positionDataSource = new MatTreeNestedDataSource<ExplorerTreeNode>();
    public positionTreeData: ExplorerTreeNode[] = [];

    // === Hat View Tree ===
    public hatTreeControl = new NestedTreeControl<ExplorerTreeNode>((node) => node.children);
    public hatDataSource = new MatTreeNestedDataSource<ExplorerTreeNode>();
    public hatTreeData: ExplorerTreeNode[] = [];

    // === Search ===
    public searchTerm = '';
    public positionSearchTerm = '';
    public matchedPositionNames: string[] = [];
    public allPositionNames: string[] = [];
    public isSearching = false;

    // === Filters ===
    public specialRightsFilter = false;
    public specialRightsCount = 0;
    public noUsersFilter = false;
    public noUsersCount = 0;
    public multiUserFilter = false;
    public multiUserCount = 0;
    public noPoliciesFilter = false;
    public noPoliciesCount = 0;
    public noSgFilter = false;
    public noSgCount = 0;
    public noPositionsFilter = false;
    public noPositionsCount = 0;
    public postsFilter = false;
    public postsCount = 0;

    // === Stats ===
    public stats = {
        totalPositions: 0,
        totalHats: 0,
        totalUsers: 0,
        totalPolicies: 0,
        totalSecurityGroups: 0,
    };

    // === Icons ===
    private partTypeIcons: Record<string, string> = {
        executive: 'business',
        division: 'account_tree',
        department: 'folder',
        section: 'folder_open',
        unit: 'workspaces',
        post: 'person',
    };

    constructor(
        public dialogRef: MatDialogRef<OrgHatExplorerDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: HatExplorerDialogData
    ) {}

    ngOnInit(): void {
        this.allPositionNames = this.getAllPositionNames();
        this.buildPositionTree();
        this.buildHatTree();
        this.calculateStats();
        this.subscribeToOrgUpdates();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private subscribeToOrgUpdates(): void {
        this.obService.organization$.pipe(takeUntil(this.destroy$)).subscribe((org) => {
            if (!org) return;
            this.data.organization = org;
            this.allPositionNames = this.getAllPositionNames();
            this.buildPositionTree();
            this.buildHatTree();
            this.calculateStats();
        });
    }

    // ============================================================
    // TREE BUILDING - POSITION VIEW
    // ============================================================

    /**
     * Build tree: Position → Hats → Users/Policies/Security Groups
     */
    private buildPositionTree(): void {
        const org = this.data.organization;
        const orgParts = org?.orgParts || [];
        const orgHats = org?.orgHats;

        // Create a map of partKey -> OrgPart
        const partMap = new Map<string, OrgPart>();
        for (const part of orgParts) {
            const partKey = part.partKey || part.key || '';
            if (partKey) partMap.set(partKey, part);
        }

        // Build tree from flat array
        const nodeMap = new Map<string, ExplorerTreeNode>();
        const rootNodes: ExplorerTreeNode[] = [];

        // First pass: create all position nodes
        for (const part of orgParts) {
            const partKey = part.partKey || part.key || '';
            if (!partKey) continue;

            const node: ExplorerTreeNode = {
                key: partKey,
                name: part.name || part.mainTitle || partKey,
                type: 'position',
                icon: this.partTypeIcons[part.partType || ''] || 'work',
                children: [],
                details: part.partType ? `(${part.partType})` : undefined,
                hierarchy: this.buildHierarchyPath(part, partMap),
                partType: part.partType || undefined,
            };
            nodeMap.set(partKey, node);
        }

        // Second pass: build hierarchy and add hats
        for (const part of orgParts) {
            const partKey = part.partKey || part.key || '';
            const node = nodeMap.get(partKey);
            if (!node) continue;

            // Find hats on this position
            const hatsOnPosition = this.getHatsOnPosition(partKey, orgHats);
            for (const hat of hatsOnPosition) {
                const hatNode = this.createHatNode(hat, org);
                node.children.push(hatNode);
            }

            // Add to parent or root
            const parent = part.parentPartKey ? nodeMap.get(part.parentPartKey) : null;
            if (parent) {
                // Insert position nodes first, then hat nodes
                const positionChildren = parent.children.filter((c) => c.type === 'position');
                const hatChildren = parent.children.filter((c) => c.type !== 'position');
                parent.children = [...positionChildren, node, ...hatChildren];
            } else {
                rootNodes.push(node);
            }
        }

        // Collect positionless hats (org-wide / special rights)
        if (orgHats?.hatsKeys) {
            const positionlessHats: ExplorerTreeNode[] = [];
            for (const hatKey of orgHats.hatsKeys) {
                const hat = orgHats[hatKey] as OrgHat;
                if (!hat || (hat as any).isSoftDeleted) continue;
                if (!hat.positionArray?.length) {
                    positionlessHats.push(this.createHatNode(hat, org));
                }
            }
            if (positionlessHats.length > 0) {
                this.sortNodesRecursively(positionlessHats);
                rootNodes.push({
                    key: '_orgwide_hats',
                    name: `Org-wide Hats (${positionlessHats.length})`,
                    type: 'label',
                    icon: 'shield',
                    children: positionlessHats,
                });
            }
        }

        // Sort nodes
        this.sortNodesRecursively(rootNodes);

        this.positionTreeData = rootNodes;
        this.positionDataSource.data = rootNodes;

        // Expand first two levels
        this.expandToLevel(this.positionTreeControl, rootNodes, 2);
    }

    /**
     * Create a hat node with its children (users, policies, security groups)
     */
    private createHatNode(hat: OrgHat, org: Organization): ExplorerTreeNode {
        const hatKey = hat.key || '';
        const hatNode: ExplorerTreeNode = {
            key: hatKey,
            name: hat.name || hatKey,
            type: 'hat',
            icon: 'school',
            children: [],
            details:
                [hat.isMultiUsers ? '(Multi-User)' : '', hat.isSpecialRights ? '(Special Rights)' : '']
                    .filter(Boolean)
                    .join(' ') || undefined,
            isSpecialRights: hat.isSpecialRights || false,
            userCount: (hat.assignedToArray || hat.userKeys || []).length,
            policyCount: this.getPoliciesForHat(hatKey, org).length,
            securityGroupCount: (hat.securityGroups || []).length,
            positionCount: (hat.positionArray || []).length,
            isMultiUsers: hat.isMultiUsers || false,
            isPostHat: this.isHatLinkedToPost(hat, org),
        };

        // Add assigned users
        const assignedUsers = hat.assignedToArray || hat.userKeys || [];
        if (assignedUsers.length > 0) {
            const usersLabel: ExplorerTreeNode = {
                key: `${hatKey}_users`,
                name: `Assigned Users (${assignedUsers.length})`,
                type: 'label',
                icon: 'people',
                children: [],
            };
            for (const userKey of assignedUsers) {
                const userName = this.getUserName(userKey, org);
                usersLabel.children.push({
                    key: userKey,
                    name: userName,
                    type: 'user',
                    icon: 'person',
                    children: [],
                });
            }
            hatNode.children.push(usersLabel);
        }

        // Add security groups
        const securityGroups = hat.securityGroups || [];
        if (securityGroups.length > 0) {
            const sgLabel: ExplorerTreeNode = {
                key: `${hatKey}_sg`,
                name: `Security Groups (${securityGroups.length})`,
                type: 'label',
                icon: 'security',
                children: [],
            };
            for (const sg of securityGroups) {
                sgLabel.children.push({
                    key: sg.key || '',
                    name: sg.name || sg.key || 'Unknown',
                    type: 'security-group',
                    icon: 'verified_user',
                    children: [],
                });
            }
            hatNode.children.push(sgLabel);
        }

        // Add policies - query from orgPolicies where policy targets this hat
        const policies = this.getPoliciesForHat(hatKey, org);
        if (policies.length > 0) {
            const policyLabel: ExplorerTreeNode = {
                key: `${hatKey}_policies`,
                name: `Policies (${policies.length})`,
                type: 'label',
                icon: 'policy',
                children: [],
            };
            for (const policyInfo of policies) {
                policyLabel.children.push({
                    key: policyInfo.key,
                    name: policyInfo.name,
                    type: 'policy',
                    icon: 'description',
                    children: [],
                    status: policyInfo.status,
                });
            }
            hatNode.children.push(policyLabel);
        }

        return hatNode;
    }

    // ============================================================
    // TREE BUILDING - HAT VIEW
    // ============================================================

    /**
     * Build tree: Hat → Positions where found, Users, Policies, Security Groups
     */
    private buildHatTree(): void {
        const org = this.data.organization;
        const orgHats = org?.orgHats;
        const rootNodes: ExplorerTreeNode[] = [];

        if (!orgHats?.hatsKeys) {
            this.hatTreeData = [];
            this.hatDataSource.data = [];
            return;
        }

        for (const hatKey of orgHats.hatsKeys) {
            const hat = orgHats[hatKey] as OrgHat;
            if (!hat) continue;
            // Skip soft-deleted hats
            if ((hat as any).isSoftDeleted) continue;

            const hatNode: ExplorerTreeNode = {
                key: hatKey,
                name: hat.name || hatKey,
                type: 'hat',
                icon: 'school',
                children: [],
                details:
                    [
                        hat.isMultiUsers ? 'Multi-Staff' : '',
                        hat.isSpecialRights ? 'Special Rights' : '',
                        !hat.positionArray?.length && !hat.isSpecialRights ? 'Org-wide' : '',
                    ]
                        .filter(Boolean)
                        .join(', ') || undefined,
                isSpecialRights: hat.isSpecialRights || false,
                userCount: (hat.assignedToArray || hat.userKeys || []).length,
                policyCount: this.getPoliciesForHat(hatKey, org).length,
                securityGroupCount: (hat.securityGroups || []).length,
                positionCount: (hat.positionArray || []).length,
                isMultiUsers: hat.isMultiUsers || false,
                isPostHat: this.isHatLinkedToPost(hat, org),
            };

            // Add positions where this hat is found
            const positions = hat.positionArray || [];
            if (positions.length > 0) {
                const posLabel: ExplorerTreeNode = {
                    key: `${hatKey}_positions`,
                    name: `Found on Positions (${positions.length})`,
                    type: 'label',
                    icon: 'place',
                    children: [],
                };
                for (const posKey of positions) {
                    const posName = this.getPositionName(posKey, org);
                    const hierarchy = this.getPositionHierarchy(posKey, org);
                    const partType = this.getPartType(posKey, org);
                    posLabel.children.push({
                        key: posKey,
                        name: posName,
                        type: 'position',
                        icon: this.partTypeIcons[partType || ''] || 'work',
                        children: [],
                        hierarchy,
                        partType: partType || undefined,
                        details: partType ? `(${partType})` : undefined,
                    });
                }
                hatNode.children.push(posLabel);
            }

            // Add assigned users
            const assignedUsers = hat.assignedToArray || hat.userKeys || [];
            if (assignedUsers.length > 0) {
                const usersLabel: ExplorerTreeNode = {
                    key: `${hatKey}_users`,
                    name: `Assigned Users (${assignedUsers.length})`,
                    type: 'label',
                    icon: 'people',
                    children: [],
                };
                for (const userKey of assignedUsers) {
                    const userName = this.getUserName(userKey, org);
                    usersLabel.children.push({
                        key: userKey,
                        name: userName,
                        type: 'user',
                        icon: 'person',
                        children: [],
                    });
                }
                hatNode.children.push(usersLabel);
            }

            // Add security groups
            const securityGroups = hat.securityGroups || [];
            if (securityGroups.length > 0) {
                const sgLabel: ExplorerTreeNode = {
                    key: `${hatKey}_sg`,
                    name: `Security Groups (${securityGroups.length})`,
                    type: 'label',
                    icon: 'security',
                    children: [],
                };
                for (const sg of securityGroups) {
                    sgLabel.children.push({
                        key: sg.key || '',
                        name: sg.name || sg.key || 'Unknown',
                        type: 'security-group',
                        icon: 'verified_user',
                        children: [],
                    });
                }
                hatNode.children.push(sgLabel);
            }

            // Add policies - query from orgPolicies where policy targets this hat
            const policies = this.getPoliciesForHat(hatKey, org);
            if (policies.length > 0) {
                const policyLabel: ExplorerTreeNode = {
                    key: `${hatKey}_policies`,
                    name: `Policies (${policies.length})`,
                    type: 'label',
                    icon: 'policy',
                    children: [],
                };
                for (const policyInfo of policies) {
                    policyLabel.children.push({
                        key: policyInfo.key,
                        name: policyInfo.name,
                        type: 'policy',
                        icon: 'description',
                        children: [],
                        status: policyInfo.status,
                    });
                }
                hatNode.children.push(policyLabel);
            }

            rootNodes.push(hatNode);
        }

        // Sort by name
        rootNodes.sort((a, b) => a.name.localeCompare(b.name));

        this.hatTreeData = rootNodes;
        this.hatDataSource.data = rootNodes;

        // Expand first level
        this.expandToLevel(this.hatTreeControl, rootNodes, 1);
    }

    // ============================================================
    // HELPER METHODS
    // ============================================================

    private getHatsOnPosition(partKey: string, orgHats: Organization['orgHats']): OrgHat[] {
        const hats: OrgHat[] = [];
        if (!orgHats?.hatsKeys) return hats;

        for (const hatKey of orgHats.hatsKeys) {
            const hat = orgHats[hatKey] as OrgHat;
            // Skip soft-deleted hats
            if (hat && !(hat as any).isSoftDeleted && hat.positionArray?.includes(partKey)) {
                hats.push(hat);
            }
        }
        return hats;
    }

    private getUserName(userKey: string, org: Organization): string {
        const orgUsers = org?.orgUsers;
        if (orgUsers && orgUsers[userKey]) {
            const user = orgUsers[userKey] as OrgUser;
            return user.name || user.email || userKey;
        }
        return userKey;
    }

    private getSecurityGroupName(sgKey: string, org: Organization): string {
        const securityGroups = org?.orgSecurityGroups;
        if (securityGroups && securityGroups[sgKey]) {
            const sg = securityGroups[sgKey];
            return (sg as { name?: string }).name || sgKey;
        }
        return sgKey;
    }

    private getPolicyName(policyKey: string, org: Organization): string {
        const orgPolicies = org?.orgPolicies;
        if (orgPolicies && orgPolicies[policyKey]) {
            const policy = orgPolicies[policyKey] as OrgPolicy;
            return policy.name || policyKey;
        }
        return policyKey;
    }

    /**
     * Get policy info including name and status
     */
    private getPolicyInfo(policyKey: string, org: Organization): { name: string; status?: PolicyStatus } {
        const orgPolicies = org?.orgPolicies;
        if (orgPolicies && orgPolicies[policyKey]) {
            const policy = orgPolicies[policyKey] as OrgPolicy;
            return {
                name: policy.name || policyKey,
                status: policy.status,
            };
        }
        return { name: policyKey };
    }

    /**
     * Check if a hat is linked to a post-type position (matches list dialog logic)
     */
    private isHatLinkedToPost(hat: OrgHat, org: Organization): boolean {
        if (!hat?.positionArray || !Array.isArray(hat.positionArray)) return false;
        const orgParts = org.orgParts || [];
        return hat.positionArray.some((partKey: string) => {
            const part = orgParts.find((p) => p.key === partKey || p.partKey === partKey);
            if (!part) return false;
            const partType = part.partType ? part.partType.toLowerCase() : '';
            if (partType) return partType === 'post';
            const depth = part.depth || 0;
            return depth >= 6;
        });
    }

    /**
     * Get policies that target a specific hat
     * Uses policy.targets.resolvedHatKeys as source of truth (not hat.policyKeys)
     */
    private getPoliciesForHat(
        hatKey: string,
        org: Organization
    ): { key: string; name: string; status?: PolicyStatus }[] {
        const policies: { key: string; name: string; status?: PolicyStatus }[] = [];
        const orgPolicies = org?.orgPolicies || {};

        for (const [policyKey, policyData] of Object.entries(orgPolicies)) {
            const policy = policyData as OrgPolicy | undefined;
            if (!policy || policy.status !== 'published') continue;

            // Check if this hat is in the policy's resolved targets
            if (policy.targets?.resolvedHatKeys?.includes(hatKey)) {
                policies.push({
                    key: policyKey,
                    name: policy.name || policyKey,
                    status: policy.status,
                });
            }
        }

        return policies;
    }

    /**
     * Get display text for policy status
     */
    public getStatusLabel(status?: PolicyStatus): string {
        if (!status) return '';
        const labels: Record<PolicyStatus, string> = {
            draft: 'Draft',
            pending_approval: 'Pending',
            approved: 'Approved',
            scheduled: 'Scheduled',
            published: 'Published',
            expired: 'Expired',
            cancelled: 'Cancelled',
            revoked: 'Revoked',
            archived: 'Archived',
            deleted: 'Deleted',
        };
        return labels[status] || status;
    }

    private getPositionName(partKey: string, org: Organization): string {
        const orgParts = org?.orgParts || [];
        for (const part of orgParts) {
            if ((part.partKey || part.key) === partKey) {
                return part.name || part.mainTitle || partKey;
            }
        }
        return partKey;
    }

    private getPartType(partKey: string, org: Organization): string {
        const orgParts = org?.orgParts || [];
        for (const part of orgParts) {
            if ((part.partKey || part.key) === partKey) {
                return part.partType || '';
            }
        }
        return '';
    }

    private getPositionHierarchy(partKey: string, org: Organization): string {
        const orgParts = org?.orgParts || [];
        const partMap = new Map<string, OrgPart>();
        for (const part of orgParts) {
            partMap.set(part.partKey || part.key || '', part);
        }

        const part = partMap.get(partKey);
        if (!part) return '';

        return this.buildHierarchyPath(part, partMap);
    }

    private buildHierarchyPath(part: OrgPart, partMap: Map<string, OrgPart>): string {
        const path: string[] = [];
        let current: OrgPart | undefined = part;

        while (current?.parentPartKey) {
            const parent = partMap.get(current.parentPartKey);
            if (parent) {
                path.unshift(parent.name || parent.mainTitle || parent.partKey || '');
            }
            current = parent;
        }

        return path.join(' → ');
    }

    private sortNodesRecursively(nodes: ExplorerTreeNode[]): void {
        // Sort: positions first, then hats, then others
        nodes.sort((a, b) => {
            if (a.type === 'position' && b.type !== 'position') return -1;
            if (a.type !== 'position' && b.type === 'position') return 1;
            return a.name.localeCompare(b.name);
        });
        for (const node of nodes) {
            if (node.children.length > 0) {
                this.sortNodesRecursively(node.children);
            }
        }
    }

    private expandToLevel(
        treeControl: NestedTreeControl<ExplorerTreeNode>,
        nodes: ExplorerTreeNode[],
        maxLevel: number,
        currentLevel = 0
    ): void {
        if (currentLevel >= maxLevel) return;
        for (const node of nodes) {
            treeControl.expand(node);
            if (node.children.length > 0) {
                this.expandToLevel(treeControl, node.children, maxLevel, currentLevel + 1);
            }
        }
    }

    // ============================================================
    // STATS
    // ============================================================

    private calculateStats(): void {
        const org = this.data.organization;
        this.stats.totalPositions = org?.orgParts?.length || 0;
        this.stats.totalHats = org?.orgHats?.hatsKeys?.length || 0;

        // Count unique users
        const userSet = new Set<string>();
        if (org?.orgHats?.hatsKeys) {
            for (const hatKey of org.orgHats.hatsKeys) {
                const hat = org.orgHats[hatKey] as OrgHat;
                if (hat) {
                    const users = hat.assignedToArray || hat.userKeys || [];
                    users.forEach((u) => userSet.add(u));
                }
            }
        }
        this.stats.totalUsers = userSet.size;

        // Count published policies that target any hat
        const policySet = new Set<string>();
        const orgPolicies = org?.orgPolicies || {};
        for (const [policyKey, policyData] of Object.entries(orgPolicies)) {
            const policy = policyData as OrgPolicy | undefined;
            if (policy?.status === 'published' && policy.targets?.resolvedHatKeys?.length) {
                policySet.add(policyKey);
            }
        }
        this.stats.totalPolicies = policySet.size;

        // Count security groups on hats
        const sgSet = new Set<string>();
        if (org?.orgHats?.hatsKeys) {
            for (const hatKey of org.orgHats.hatsKeys) {
                const hat = org.orgHats[hatKey] as OrgHat;
                if (hat?.securityGroups) {
                    hat.securityGroups.forEach((sg) => sgSet.add(sg.key || ''));
                }
            }
        }
        this.stats.totalSecurityGroups = sgSet.size;

        // Count special rights hats and filter counts
        const hats: OrgHat[] = [];
        if (org?.orgHats?.hatsKeys) {
            for (const hatKey of org.orgHats.hatsKeys) {
                const hat = org.orgHats[hatKey] as OrgHat;
                if (hat && !(hat as any).isSoftDeleted) hats.push(hat);
            }
        }
        this.specialRightsCount = hats.filter((h) => h.isSpecialRights).length;
        this.noUsersCount = hats.filter((h) => !h.assignedToArray?.length && !h.userKeys?.length).length;
        this.multiUserCount = hats.filter((h) => h.isMultiUsers).length;
        this.noPoliciesCount = hats.filter((h) => this.getPoliciesForHat(h.key || '', org).length === 0).length;
        this.noSgCount = hats.filter((h) => !h.securityGroups?.length).length;
        this.noPositionsCount = hats.filter((h) => !h.positionArray?.length).length;
        this.postsCount = hats.filter((h) => this.isHatLinkedToPost(h, org)).length;
    }

    // ============================================================
    // SEARCH
    // ============================================================

    public applySearch(): void {
        const term = this.searchTerm.trim().toLowerCase();
        const posTerm = this.positionSearchTerm.trim().toLowerCase();
        this.isSearching = !!term || !!posTerm;
        const hasActiveFilter =
            this.specialRightsFilter ||
            this.noUsersFilter ||
            this.multiUserFilter ||
            this.noPoliciesFilter ||
            this.noSgFilter ||
            this.noPositionsFilter ||
            this.postsFilter;

        // Compute position-related keys if position filter is active
        let positionRelatedKeys: Set<string> | null = null;
        if (posTerm) {
            this.matchedPositionNames = this.getAllPositionNames().filter((name) =>
                this.fuzzyMatchPosition(name.toLowerCase(), posTerm)
            );
            if (this.matchedPositionNames.length > 0) {
                const matchingPartKeys = this.getMatchingPositionKeys(posTerm);
                positionRelatedKeys = this.computePositionRelatedKeys(matchingPartKeys);
            } else {
                positionRelatedKeys = new Set();
            }
        } else {
            this.matchedPositionNames = [];
        }

        if (!term && !positionRelatedKeys && !hasActiveFilter) {
            this.positionDataSource.data = this.positionTreeData;
            this.hatDataSource.data = this.hatTreeData;
            this.expandToLevel(this.positionTreeControl, this.positionTreeData, 2);
            this.expandToLevel(this.hatTreeControl, this.hatTreeData, 1);
            return;
        }

        // Build hat predicate from active filters
        const hatPredicate = this.buildHatFilterPredicate();

        // Filter position tree
        let filteredPositions = this.positionTreeData;
        if (positionRelatedKeys) filteredPositions = this.filterByKeys(filteredPositions, positionRelatedKeys);
        if (term) filteredPositions = this.filterBySearchTerm(filteredPositions, term);
        if (hatPredicate) filteredPositions = this.filterByHatPredicate(filteredPositions, hatPredicate);
        this.positionDataSource.data = filteredPositions;
        this.expandAllNodes(this.positionTreeControl, filteredPositions);

        // Filter hat tree
        let filteredHats = this.hatTreeData;
        if (positionRelatedKeys) filteredHats = this.filterByKeys(filteredHats, positionRelatedKeys);
        if (term) filteredHats = this.filterBySearchTerm(filteredHats, term);
        if (hatPredicate) filteredHats = this.filterByHatPredicate(filteredHats, hatPredicate);
        this.hatDataSource.data = filteredHats;
        this.expandAllNodes(this.hatTreeControl, filteredHats);
    }

    private filterByKeys(nodes: ExplorerTreeNode[], keys: Set<string>): ExplorerTreeNode[] {
        const results: ExplorerTreeNode[] = [];
        for (const node of nodes) {
            const filteredChildren = this.filterByKeys(node.children, keys);
            if (keys.has(node.key) || filteredChildren.length > 0) {
                results.push({
                    ...node,
                    children: keys.has(node.key) ? node.children : filteredChildren,
                    matchesSearch: keys.has(node.key),
                });
            }
        }
        return results;
    }

    private filterBySearchTerm(nodes: ExplorerTreeNode[], term: string): ExplorerTreeNode[] {
        const results: ExplorerTreeNode[] = [];
        for (const node of nodes) {
            const matches =
                this.fuzzyMatchText(node.name.toLowerCase(), term) ||
                (!!node.details && this.fuzzyMatchText(node.details.toLowerCase(), term)) ||
                (!!node.hierarchy && this.fuzzyMatchText(node.hierarchy.toLowerCase(), term));
            const filteredChildren = this.filterBySearchTerm(node.children, term);
            if (matches || filteredChildren.length > 0) {
                results.push({
                    ...node,
                    children: matches ? node.children : filteredChildren,
                    matchesSearch: matches,
                });
            }
        }
        return results;
    }

    private expandAllNodes(treeControl: NestedTreeControl<ExplorerTreeNode>, nodes: ExplorerTreeNode[]): void {
        for (const node of nodes) {
            treeControl.expand(node);
            if (node.children.length > 0) {
                this.expandAllNodes(treeControl, node.children);
            }
        }
    }

    public clearSearch(): void {
        this.searchTerm = '';
        this.applySearch();
    }

    public clearPositionSearch(): void {
        this.positionSearchTerm = '';
        this.applySearch();
    }

    public onSearchChanged(term: string): void {
        this.searchTerm = term;
        this.applySearch();
    }

    public onPositionSearchChanged(result: { term: string; matchedNames: string[] }): void {
        this.positionSearchTerm = result.term;
        this.applySearch();
    }

    public toggleSpecialRightsFilter(): void {
        this.specialRightsFilter = !this.specialRightsFilter;
        this.applySearch();
    }

    public toggleNoUsersFilter(): void {
        this.noUsersFilter = !this.noUsersFilter;
        this.applySearch();
    }

    public toggleMultiUserFilter(): void {
        this.multiUserFilter = !this.multiUserFilter;
        this.applySearch();
    }

    public toggleNoPoliciesFilter(): void {
        this.noPoliciesFilter = !this.noPoliciesFilter;
        this.applySearch();
    }

    public toggleNoSgFilter(): void {
        this.noSgFilter = !this.noSgFilter;
        this.applySearch();
    }

    public toggleNoPositionsFilter(): void {
        this.noPositionsFilter = !this.noPositionsFilter;
        this.applySearch();
    }

    public togglePostsFilter(): void {
        this.postsFilter = !this.postsFilter;
        this.applySearch();
    }

    public clearFilters(): void {
        this.specialRightsFilter = false;
        this.noUsersFilter = false;
        this.multiUserFilter = false;
        this.noPoliciesFilter = false;
        this.noSgFilter = false;
        this.noPositionsFilter = false;
        this.postsFilter = false;
        this.applySearch();
    }

    /** Mobile-only: collapse the filter chips behind a "Filters" toggle (parity
        with the Staff list dialog). Ignored on desktop where chips always show. */
    public filtersExpanded = false;
    public toggleFilters(): void {
        this.filtersExpanded = !this.filtersExpanded;
    }

    public get hasActiveFilter(): boolean {
        return (
            this.specialRightsFilter ||
            this.noUsersFilter ||
            this.multiUserFilter ||
            this.noPoliciesFilter ||
            this.noSgFilter ||
            this.noPositionsFilter ||
            this.postsFilter
        );
    }

    private buildHatFilterPredicate(): ((node: ExplorerTreeNode) => boolean) | null {
        if (!this.hasActiveFilter) return null;
        return (node: ExplorerTreeNode) => {
            if (this.noUsersFilter && (node.userCount ?? 0) > 0) return false;
            if (this.multiUserFilter && !node.isMultiUsers) return false;
            if (this.noPoliciesFilter && (node.policyCount ?? 0) > 0) return false;
            if (this.noSgFilter && (node.securityGroupCount ?? 0) > 0) return false;
            if (this.noPositionsFilter && (node.positionCount ?? 0) > 0) return false;
            if (this.postsFilter && !node.isPostHat) return false;
            if (this.specialRightsFilter && !node.isSpecialRights) return false;
            return true;
        };
    }

    private filterByHatPredicate(
        nodes: ExplorerTreeNode[],
        predicate: (node: ExplorerTreeNode) => boolean
    ): ExplorerTreeNode[] {
        const results: ExplorerTreeNode[] = [];
        for (const node of nodes) {
            if (node.type === 'hat') {
                if (predicate(node)) results.push(node);
            } else if (node.children.length > 0) {
                const filteredChildren = this.filterByHatPredicate(node.children, predicate);
                if (filteredChildren.length > 0) {
                    results.push({ ...node, children: filteredChildren });
                }
            }
        }
        return results;
    }

    private getAllPositionNames(): string[] {
        const names = new Set<string>();
        const orgParts = this.data.organization?.orgParts || [];
        for (const part of orgParts) {
            const name = part.name || part.mainTitle;
            if (name) names.add(name);
        }
        return Array.from(names).sort();
    }

    private getMatchingPositionKeys(posTerm: string): Set<string> {
        const keys = new Set<string>();
        const orgParts = this.data.organization?.orgParts || [];
        for (const part of orgParts) {
            const name = (part.name || part.mainTitle || '').toLowerCase();
            if (this.fuzzyMatchPosition(name, posTerm)) {
                keys.add(part.partKey || part.key || '');
            }
        }
        return keys;
    }

    private computePositionRelatedKeys(matchingPositionKeys: Set<string>): Set<string> {
        const keys = new Set<string>(matchingPositionKeys);
        const org = this.data.organization;
        const orgHats = org?.orgHats;
        if (!orgHats?.hatsKeys) return keys;

        for (const hatKey of orgHats.hatsKeys) {
            const hat = orgHats[hatKey] as OrgHat;
            if (!hat) continue;
            const positions = hat.positionArray || [];
            if (positions.some((pk: string) => matchingPositionKeys.has(pk))) {
                keys.add(hatKey);
                const users = hat.assignedToArray || hat.userKeys || [];
                users.forEach((u: string) => keys.add(u));
            }
        }
        return keys;
    }

    private fuzzyMatchText(text: string, searchTerm: string): boolean {
        if (text.includes(searchTerm)) return true;
        const textWords = text.split(/\s+/);
        const searchWords = searchTerm.split(/\s+/).filter((w) => w.length > 0);
        if (searchWords.length > 1) {
            return searchWords.every((sw) => textWords.some((tw) => this.fuzzyMatchWord(tw, sw)));
        }
        return textWords.some((tw) => this.fuzzyMatchWord(tw, searchTerm));
    }

    private fuzzyMatchPosition(positionName: string, searchTerm: string): boolean {
        if (positionName.includes(searchTerm)) return true;
        const posWords = positionName.split(/\s+/);
        const searchWords = searchTerm.split(/\s+/).filter((w) => w.length > 0);
        if (searchWords.length > 1) {
            return searchWords.every((sw) => posWords.some((pw) => this.fuzzyMatchWord(pw, sw)));
        }
        return posWords.some((pw) => this.fuzzyMatchWord(pw, searchTerm));
    }

    private fuzzyMatchWord(word: string, searchWord: string): boolean {
        if (word.includes(searchWord)) return true;
        const maxDist = searchWord.length <= 2 ? 0 : Math.floor(searchWord.length / 3);
        if (this.levenshtein(word, searchWord) <= maxDist) return true;
        if (word.length >= searchWord.length) {
            const prefix = word.substring(0, searchWord.length + 1);
            if (this.levenshtein(prefix, searchWord) <= maxDist) return true;
        }
        return false;
    }

    private levenshtein(a: string, b: string): number {
        const m = a.length,
            n = b.length;
        const dp: number[] = Array.from({ length: n + 1 }, (_, i) => i);
        for (let i = 1; i <= m; i++) {
            let prev = dp[0];
            dp[0] = i;
            for (let j = 1; j <= n; j++) {
                const temp = dp[j];
                dp[j] = a[i - 1] === b[j - 1] ? prev : 1 + Math.min(prev, dp[j], dp[j - 1]);
                prev = temp;
            }
        }
        return dp[n];
    }

    // ============================================================
    // VIEW MODE
    // ============================================================

    public setViewMode(mode: ExplorerViewMode): void {
        this.viewMode = mode;
        if (this.searchTerm || this.positionSearchTerm) {
            this.applySearch();
        }
    }

    // ============================================================
    // TREE HELPERS FOR TEMPLATE
    // ============================================================

    public hasChild = (_: number, node: ExplorerTreeNode): boolean => {
        return node.children && node.children.length > 0;
    };

    public expandAll(): void {
        if (this.viewMode === 'positions') {
            this.expandAllNodes(this.positionTreeControl, this.positionDataSource.data);
        } else {
            this.expandAllNodes(this.hatTreeControl, this.hatDataSource.data);
        }
    }

    public collapseAll(): void {
        if (this.viewMode === 'positions') {
            this.positionTreeControl.collapseAll();
        } else {
            this.hatTreeControl.collapseAll();
        }
    }
}
